/** Thrown when a caller tries to drive the session into an illegal transition. */
export class SessionStateError extends Error {
    from;
    attempted;
    constructor(from, attempted) {
        super(`Cannot ${attempted} from state '${from}'`);
        this.from = from;
        this.attempted = attempted;
        this.name = 'SessionStateError';
    }
}
const monotonicFallback = () => typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
export class GameSession {
    #publicId;
    #gameSlug;
    #residentId;
    #surface;
    #inputMode;
    #deviceKind;
    #clientAppVersion;
    #difficulty;
    #transport;
    #now;
    #monotonicNow;
    #state = 'idle';
    #seq = 0;
    #startMonotonicMs = null;
    #endMonotonicMs = null;
    constructor(opts) {
        this.#publicId = opts.publicId ?? crypto.randomUUID();
        this.#gameSlug = opts.gameSlug;
        this.#residentId = opts.residentId;
        this.#surface = opts.surface;
        this.#inputMode = opts.inputMode;
        this.#deviceKind = opts.deviceKind;
        this.#clientAppVersion = opts.clientAppVersion;
        this.#difficulty = opts.difficulty;
        this.#transport = opts.transport;
        this.#now = opts.now ?? (() => new Date());
        this.#monotonicNow = opts.monotonicNow ?? monotonicFallback;
    }
    // ---- read-only getters ---------------------------------------------------
    get state() {
        return this.#state;
    }
    get publicId() {
        return this.#publicId;
    }
    get gameSlug() {
        return this.#gameSlug;
    }
    get residentId() {
        return this.#residentId;
    }
    get eventCount() {
        return this.#seq;
    }
    get elapsedMs() {
        if (this.#startMonotonicMs === null)
            return 0;
        const end = this.#endMonotonicMs ?? this.#monotonicNow();
        return Math.max(0, Math.floor(end - this.#startMonotonicMs));
    }
    // ---- lifecycle -----------------------------------------------------------
    /** Begin the session. Emits `session_started` + a `game_start` event. */
    async start() {
        if (this.#state !== 'idle') {
            throw new SessionStateError(this.#state, 'start');
        }
        this.#startMonotonicMs = this.#monotonicNow();
        this.#state = 'playing';
        await this.#transport.send({
            kind: 'session_started',
            payload: {
                sessionPublicId: this.#publicId,
                gameSlug: this.#gameSlug,
                difficultyLevel: this.#difficulty,
            },
        });
        await this.#appendEvent('game_start', {
            surface: this.#surface,
            inputMode: this.#inputMode,
            deviceKind: this.#deviceKind,
            clientAppVersion: this.#clientAppVersion,
            residentId: this.#residentId,
            difficultyLevel: this.#difficulty,
        });
    }
    /**
     * Append one event to the telemetry stream. Games call this from inside
     * their loop — `onCorrect`, `onIncorrect`, `roundStart`, etc.
     *
     * `reactionTimeMs` is the contract's per-trial response time (see
     * {@link GameEvent}). Pass null for non-response events.
     */
    async emit(eventType, payload = {}, reactionTimeMs = null) {
        if (this.#state === 'idle' || this.#state === 'ended') {
            throw new SessionStateError(this.#state, `emit(${eventType})`);
        }
        await this.#appendEvent(eventType, payload, reactionTimeMs);
    }
    /**
     * Pause the session. Idempotent: pausing while already paused returns
     * silently. Pause requests can race with autopause heuristics and games
     * shouldn't have to track that themselves.
     */
    async pause() {
        if (this.#state === 'paused')
            return;
        if (this.#state !== 'playing') {
            throw new SessionStateError(this.#state, 'pause');
        }
        this.#state = 'paused';
        await this.#appendEvent('pause');
    }
    async resume() {
        if (this.#state === 'playing')
            return;
        if (this.#state !== 'paused') {
            throw new SessionStateError(this.#state, 'resume');
        }
        this.#state = 'playing';
        await this.#appendEvent('resume');
    }
    /** Normal end-of-game. Records duration and emits `session_ended`. */
    async end(args) {
        await this.#finalize(args, 'ended');
    }
    /** Player bailed. `completed` is forced to false. */
    async abandon(args) {
        await this.#finalize({ ...args, completed: false }, 'abandoned');
    }
    // ---- internals -----------------------------------------------------------
    /**
     * Bypass the ended-state guard. Used for `game_start`, `pause`, `resume`,
     * and `game_end` — events that the session itself drives.
     */
    async #appendEvent(eventType, payload = {}, reactionTimeMs = null) {
        this.#seq += 1;
        const event = {
            sessionPublicId: this.#publicId,
            seq: this.#seq,
            occurredAt: this.#now().toISOString(),
            elapsedMs: this.elapsedMs,
            eventType,
            reactionTimeMs,
            payload,
        };
        await this.#transport.send({ kind: 'queue_event', payload: event });
    }
    async #finalize(args, status) {
        if (this.#state === 'ended' || this.#state === 'idle') {
            throw new SessionStateError(this.#state, 'end');
        }
        // Freeze the clock before emitting so the game_end event and the
        // session_ended message agree on durationMs.
        this.#endMonotonicMs = this.#monotonicNow();
        // game_end is still "during" the session — emit it first.
        await this.#appendEvent('game_end', {
            finalScore: args.finalScore,
            accuracy: args.accuracy,
            completed: args.completed,
            status,
        });
        this.#state = 'ended';
        const outbound = {
            kind: 'session_ended',
            payload: {
                sessionPublicId: this.#publicId,
                gameSlug: this.#gameSlug,
                durationMs: this.elapsedMs,
                score: args.finalScore,
                accuracy: args.accuracy,
                completed: args.completed,
            },
        };
        await this.#transport.send(outbound);
    }
}
//# sourceMappingURL=session.js.map